import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import {
  TRANSFER_FLAG_REASON_LABELS,
  TRANSFER_FLAG_REASONS,
  TransferFlagReason,
} from '../types/lead-engagement';
import { useAuthenticatedRequest, UserContext } from '../UserContext';

const { Text } = Typography;
const { TextArea } = Input;

const API_BASE = '/api/lead-engagement/leads';

// Annotation event: flags a transferred fact for human review. It is not a state — the backend
// excludes it from current_state — so it renders as a badge on the transfer, not as a step.
const ATTENTION_EVENT = 'transfer_requires_human_attention';

// Event-type colors deliberately avoid green/red — those are reserved for a manual undo's
// outcome (green = clean, red = errored), so the two never get confused.
const EVENT_COLOR: Record<string, string> = {
  eligible: 'cyan',
  ineligible: 'purple',
  transferred: 'blue',
  met: 'gold',
  transacted: 'geekblue',
  deactivated: 'default',
  [ATTENTION_EVENT]: 'orange',
};

export function eventColor(event: string): string {
  return EVENT_COLOR[event] ?? 'blue';
}

const EVENT_LABELS: Record<string, string> = {
  [ATTENTION_EVENT]: 'needs human attention',
};

export function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

export interface LedgerFact {
  id: string;
  event: string;
  source: string;
  actor: string;
  reason: string | null;
  attributes: Record<string, unknown> | null;
  created_at: string;
  invalidates_id: string | null;
  lead_engagement_id: string | null;
}

// System actors are enum values (snake_case); admin corrections use the operator's email — show
// enum actors with a friendly label, emails as-is.
const ACTOR_LABELS: Record<string, string> = {
  lead_engagement: 'Lead Engagement',
  speed_to_lead: 'Speed to Lead',
  isa: 'ISA',
  appointment_outcome: 'Appointment Outcome',
};

export function actorLabel(actor: string): string {
  return ACTOR_LABELS[actor] ?? actor;
}

const ATTR_KEY_LABELS: Record<string, string> = {
  previous_stage: 'Previous stage',
  reason: 'Reason',
  outcome: 'Outcome',
  categories: 'Categories',
};

function humanizeValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(humanizeValue).join(', ');
  }
  if (typeof value !== 'string') {
    return String(value);
  }
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}

// A short, readable summary of a fact's attributes for the timeline; the full raw JSON stays
// available in the collapsible below. Opaque ids (call / workflow) collapse to a transfer-kind
// label rather than a bare UUID, and a `reason` attribute that just echoes the fact's reason is
// dropped so it isn't shown twice.
function attributeSummary(fact: LedgerFact): string[] {
  const attrs = fact.attributes ?? {};
  const lines: string[] = [];
  if (fact.event === 'transferred') {
    if ('isa_workflow_id' in attrs) {
      lines.push('3-way intro');
    } else if ('lead_call_id' in attrs) {
      lines.push('Warm transfer');
    }
  }
  for (const [key, value] of Object.entries(attrs)) {
    if (
      key === 'lead_call_id' ||
      key === 'isa_workflow_id' ||
      key === 'flagged_fact_id'
    ) {
      continue;
    }
    if (key === 'reason' && value === fact.reason) {
      continue;
    }
    const label = ATTR_KEY_LABELS[key] ?? key.replace(/_/g, ' ');
    lines.push(`${label}: ${humanizeValue(value)}`);
  }
  return lines;
}

interface LedgerData {
  lead_id: string;
  current_state: string | null;
  current_fact_id: string | null;
  facts: LedgerFact[];
}

// Live flags: attention rows that are neither corrections (invalidates_id set) nor retired by an
// unflag correction pointing at them. Returns the ids of the transferred facts they point at,
// along with the flag's categories (empty — older flags predate the field).
export function liveFlaggedFacts(
  facts: LedgerFact[],
): Map<string, TransferFlagReason[]> {
  const invalidated = new Set(
    facts.filter(f => f.invalidates_id).map(f => f.invalidates_id as string),
  );
  const targets = new Map<string, TransferFlagReason[]>();
  for (const fact of facts) {
    if (
      fact.event !== ATTENTION_EVENT ||
      fact.invalidates_id ||
      invalidated.has(fact.id)
    ) {
      continue;
    }
    const target = fact.attributes?.flagged_fact_id;
    if (typeof target === 'string') {
      const categories = fact.attributes?.categories;
      targets.set(
        target,
        Array.isArray(categories) ? (categories as TransferFlagReason[]) : [],
      );
    }
  }
  return targets;
}

interface StepResult {
  name: string;
  status: 'ok' | 'failed';
  detail: string | null;
}

interface UndoResponse {
  status: 'ok' | 'partial' | 'failed' | 'blocked';
  block_reason: string | null;
  steps: StepResult[];
  ledger: LedgerData;
}

export function LeadEngagementLedger({
  apiUrl,
  leadID,
  onStateChange,
}: {
  apiUrl: string;
  leadID: string;
  // Fired after a recorded undo so the parent can patch its Ledger State column.
  onStateChange?: (leadID: string, state: string | null) => void;
}) {
  const user = useContext(UserContext);
  const authenticatedRequest = useAuthenticatedRequest(user?.accessToken);
  const [data, setData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [target, setTarget] = useState<LedgerFact | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<UndoResponse | null>(null);
  // Per-target outcome, so an undo row can render green (clean) / red (errored).
  const [outcomeByTarget, setOutcomeByTarget] = useState<
    Record<string, UndoResponse['status']>
  >({});
  const [transactOpen, setTransactOpen] = useState(false);
  const [transactReason, setTransactReason] = useState('');
  // Optional reporting inputs — the close date the admin backfills, and the deal price.
  const [transactDate, setTransactDate] = useState<string | null>(null);
  const [transactPrice, setTransactPrice] = useState<number | null>(null);
  const [transactSubmitting, setTransactSubmitting] = useState(false);
  const [transactResult, setTransactResult] = useState<UndoResponse | null>(
    null,
  );

  // Flag / unflag ("mark potentially bad transfer") — a separate, simpler flow than undo: it
  // annotates the transfer for human review, changes no state, and its reason is optional.
  const [flagTarget, setFlagTarget] = useState<LedgerFact | null>(null);
  const [flagAction, setFlagAction] = useState<'flag' | 'unflag'>('flag');
  const [flagReason, setFlagReason] = useState('');
  // Categories are required for 'flag' (what kind(s) of bad transfer this is) and unused for
  // 'unflag'. Multiple categories can apply to a single transfer (e.g. interrupted AND silent).
  const [flagCategories, setFlagCategories] = useState<TransferFlagReason[]>(
    [],
  );
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagResult, setFlagResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  // Always-current leadID, so an in-flight undo POST that resolves after a lead switch can detect
  // it belongs to the previous lead and discard its result instead of overwriting the new lead.
  const currentLeadID = useRef(leadID);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedRequest(headers =>
          fetch(`${apiUrl}${API_BASE}/${leadID}/ledger`, {
            headers,
          }),
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = (await res.json()) as LedgerData;
        if (!cancelled) {
          setData(json);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, leadID, user?.accessToken]);

  // Switching leads must drop any in-flight undo state — otherwise the modal shows the previous
  // lead's fact and could post its id against the new lead. Keyed on leadID only so a token
  // refresh doesn't close an open modal mid-edit.
  useEffect(() => {
    currentLeadID.current = leadID;
    setTarget(null);
    setResult(null);
    setReason('');
    setOutcomeByTarget({});
    setFlagTarget(null);
    setFlagResult(null);
    setFlagReason('');
    setFlagCategories([]);
    setFlagSubmitting(false);
    // Drop the previous lead's fetched ledger + in-flight flags too, so the card can't briefly show a
    // stale current_state/timeline (or a carried-over submitting spinner) until the refetch lands.
    setData(null);
    setError(null);
    setSubmitting(false);
    setTransactOpen(false);
    setTransactReason('');
    setTransactDate(null);
    setTransactPrice(null);
    setTransactResult(null);
    setTransactSubmitting(false);
  }, [leadID]);

  // The server tells us which fact is undoable (single source of truth for the tie-break).
  const currentTopFactId = data?.current_fact_id ?? null;

  function openModal(fact: LedgerFact) {
    setTarget(fact);
    setReason('');
    setResult(null);
  }

  function closeModal() {
    setTarget(null);
    setResult(null);
  }

  async function submit() {
    if (!target || !reason.trim()) {
      return;
    }
    // Pin the lead this undo was launched against; if the user switches leads before the POST
    // resolves, a stale response must not overwrite the new lead's ledger (or arm an Undo for it).
    const submittedLeadID = leadID;
    setSubmitting(true);
    try {
      const res = await authenticatedRequest(headers =>
        fetch(`${apiUrl}${API_BASE}/${leadID}/facts/${target.id}/undo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          // actor (who performed the correction) is derived server-side from the auth token.
          body: JSON.stringify({ reason: reason.trim() }),
        }),
      );
      let json: Partial<UndoResponse> | null = null;
      try {
        json = (await res.json()) as UndoResponse;
      } catch {
        json = null;
      }
      if (currentLeadID.current !== submittedLeadID) {
        return; // lead switched mid-flight — this result is for the previous lead, discard it
      }
      // A well-formed undo outcome (ok/partial/blocked/failed) always carries a steps[] array.
      // Anything else — an auth/500 error body, non-JSON — is surfaced as a failed run so the
      // result view never dereferences a missing steps[].
      if (
        !json ||
        !Array.isArray(json.steps) ||
        typeof json.status !== 'string'
      ) {
        setResult({
          status: 'failed',
          block_reason:
            json?.block_reason ?? `Undo failed (HTTP ${res.status})`,
          steps: [],
          ledger: data as LedgerData,
        });
        return;
      }
      const outcome = json as UndoResponse;
      setResult(outcome);
      if (outcome.ledger) {
        setData(outcome.ledger);
      }
      // Only ok/partial commit an invalidation (the state changed); blocked/failed leave the ledger
      // untouched, so don't recolor the row or push a (possibly null) state to the parent's cell.
      if (outcome.status === 'ok' || outcome.status === 'partial') {
        setOutcomeByTarget(prev => ({ ...prev, [target.id]: outcome.status }));
        onStateChange?.(leadID, outcome.ledger?.current_state ?? null);
      }
    } catch (e) {
      if (currentLeadID.current !== submittedLeadID) {
        return; // lead switched mid-flight — don't surface this error against the new lead
      }
      setResult({
        status: 'failed',
        block_reason: e instanceof Error ? e.message : String(e),
        steps: [],
        ledger: data as LedgerData,
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function submitTransact() {
    // Pin the lead this was launched against; a lead switch mid-flight must not overwrite the new lead.
    const submittedLeadID = leadID;
    setTransactSubmitting(true);
    try {
      const res = await authenticatedRequest(headers =>
        fetch(`${apiUrl}${API_BASE}/${leadID}/transact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          // actor is derived server-side from the auth token.
          body: JSON.stringify({
            ...(transactReason.trim() ? { reason: transactReason.trim() } : {}),
            ...(transactDate ? { transaction_date: transactDate } : {}),
            ...(transactPrice != null ? { deal_price: transactPrice } : {}),
          }),
        }),
      );
      let json: Partial<UndoResponse> | null = null;
      try {
        json = (await res.json()) as UndoResponse;
      } catch {
        json = null;
      }
      if (currentLeadID.current !== submittedLeadID) {
        return; // lead switched mid-flight — discard this result
      }
      if (
        !json ||
        !Array.isArray(json.steps) ||
        typeof json.status !== 'string'
      ) {
        setTransactResult({
          status: 'failed',
          block_reason:
            json?.block_reason ?? `Mark transacted failed (HTTP ${res.status})`,
          steps: [],
          ledger: data as LedgerData,
        });
        return;
      }
      const outcome = json as UndoResponse;
      setTransactResult(outcome);
      if (outcome.ledger) {
        setData(outcome.ledger);
      }
      // Only ok/partial record the fact; blocked/failed leave the ledger untouched.
      if (outcome.status === 'ok' || outcome.status === 'partial') {
        onStateChange?.(leadID, outcome.ledger?.current_state ?? null);
      }
    } catch (e) {
      if (currentLeadID.current !== submittedLeadID) {
        return;
      }
      setTransactResult({
        status: 'failed',
        block_reason: e instanceof Error ? e.message : String(e),
        steps: [],
        ledger: data as LedgerData,
      });
    } finally {
      // Only clear loading if still on the same lead — a stale submit must not reset a newer
      // in-flight submit on the current lead (which would allow a duplicate POST).
      if (currentLeadID.current === submittedLeadID) {
        setTransactSubmitting(false);
      }
    }
  }

  function openFlagModal(fact: LedgerFact, action: 'flag' | 'unflag') {
    setFlagTarget(fact);
    setFlagAction(action);
    setFlagReason('');
    setFlagCategories([]);
    setFlagResult(null);
  }

  function closeFlagModal() {
    setFlagTarget(null);
    setFlagResult(null);
  }

  async function submitFlagAction() {
    if (!flagTarget || (flagAction === 'flag' && flagCategories.length === 0)) {
      return;
    }
    // Same mid-flight guard as undo: a response landing after a lead switch is discarded.
    const submittedLeadID = leadID;
    setFlagSubmitting(true);
    try {
      const res = await authenticatedRequest(headers =>
        fetch(
          `${apiUrl}${API_BASE}/${leadID}/facts/${flagTarget.id}/${flagAction}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            },
            // actor is derived server-side from the auth token; reason is optional. categories are
            // only meaningful (and required) when flagging — unflag omits them.
            body: JSON.stringify({
              reason: flagReason.trim() || null,
              ...(flagAction === 'flag' ? { categories: flagCategories } : {}),
            }),
          },
        ),
      );
      const text = await res.text();
      if (currentLeadID.current !== submittedLeadID) {
        return;
      }
      if (!res.ok) {
        setFlagResult({ message: text || `HTTP ${res.status}`, ok: false });
        return;
      }
      let json: { ledger?: LedgerData } | null = null;
      try {
        json = JSON.parse(text) as { ledger?: LedgerData };
      } catch {
        json = null;
      }
      if (json?.ledger) {
        setData(json.ledger);
      }
      setFlagResult({
        message:
          flagAction === 'flag'
            ? 'Flagged — this transfer is marked as needing human attention.'
            : 'Flag removed.',
        ok: true,
      });
    } catch (e) {
      if (currentLeadID.current !== submittedLeadID) {
        return;
      }
      setFlagResult({
        message: e instanceof Error ? e.message : String(e),
        ok: false,
      });
    } finally {
      setFlagSubmitting(false);
    }
  }

  function factDotColor(fact: LedgerFact): string {
    // A live attention flag renders orange — the visual "this needs a human" marker.
    if (fact.event === ATTENTION_EVENT && !fact.invalidates_id) {
      return 'orange';
    }
    // Regular ledger events are neutral; red/green is reserved for a manual undo's outcome.
    if (!fact.invalidates_id) {
      return 'gray';
    }
    const outcome = outcomeByTarget[fact.invalidates_id];
    if (outcome === 'ok') {
      return 'green';
    }
    if (outcome === 'partial' || outcome === 'failed') {
      return 'red';
    }
    return 'gray';
  }

  const ranResult = result && result.status !== 'blocked';
  const blocked = result?.status === 'blocked';

  let body: React.ReactNode;
  if (loading) {
    body = <Spin />;
  } else if (error) {
    body = <Text type="danger">Failed to load ledger: {error}</Text>;
  } else if (!data || data.facts.length === 0) {
    body = <Text type="secondary">No ledger facts for this lead.</Text>;
  } else {
    const undoneIds = new Set(
      data.facts
        .filter(f => f.invalidates_id)
        .map(f => f.invalidates_id as string),
    );
    const flaggedFacts = liveFlaggedFacts(data.facts);
    body = (
      <Timeline
        items={data.facts
          .slice()
          .reverse()
          .map(fact => {
            const isUndo = !!fact.invalidates_id;
            const isUndone = undoneIds.has(fact.id);
            const isFlagged = flaggedFacts.has(fact.id);
            const flagCategories = flaggedFacts.get(fact.id) ?? [];
            const outcome = isUndo
              ? outcomeByTarget[fact.invalidates_id as string]
              : undefined;
            const label = isUndo ? (
              // A manual correction row: color by the undo outcome (green = clean, red = errored).
              // Unflag corrections share the attention event, so they get their own label.
              <Tag
                color={
                  outcome === 'ok' ? 'success' : outcome ? 'error' : 'default'
                }
              >
                {fact.event === ATTENTION_EVENT
                  ? 'Removed flag'
                  : `Undone ${fact.event}`}
              </Tag>
            ) : (
              <Tag color={eventColor(fact.event)}>
                {isUndone ? (
                  <span style={{ textDecoration: 'line-through' }}>
                    {eventLabel(fact.event)}
                  </span>
                ) : (
                  eventLabel(fact.event)
                )}
              </Tag>
            );
            return {
              color: factDotColor(fact),
              children: (
                <div>
                  <Space size="small" wrap>
                    {label}
                    {isFlagged ? (
                      <Tag color="orange">
                        ⚠{' '}
                        {flagCategories.length > 0
                          ? flagCategories
                              .map(c => TRANSFER_FLAG_REASON_LABELS[c])
                              .join(', ')
                          : 'needs attention'}
                      </Tag>
                    ) : null}
                    {fact.id === currentTopFactId ? (
                      <Button
                        danger
                        onClick={() => openModal(fact)}
                        size="small"
                      >
                        Undo
                      </Button>
                    ) : null}
                    {fact.event === 'transferred' &&
                    !fact.invalidates_id &&
                    !isUndone &&
                    !isFlagged ? (
                      <Button
                        onClick={() => openFlagModal(fact, 'flag')}
                        size="small"
                      >
                        Mark bad transfer
                      </Button>
                    ) : null}
                    {fact.event === ATTENTION_EVENT &&
                    !fact.invalidates_id &&
                    !isUndone ? (
                      <Button
                        onClick={() => openFlagModal(fact, 'unflag')}
                        size="small"
                      >
                        Remove flag
                      </Button>
                    ) : null}
                  </Space>
                  <div>
                    <Tooltip title={new Date(fact.created_at).toISOString()}>
                      <Text type="secondary">
                        {new Date(fact.created_at).toLocaleString()}
                      </Text>
                    </Tooltip>
                  </div>
                  <div>
                    <Text type="secondary">
                      {isUndo
                        ? `by ${actorLabel(fact.actor)}`
                        : actorLabel(fact.actor)}
                      {fact.reason ? ` — ${fact.reason}` : ''}
                    </Text>
                  </div>
                  {fact.attributes &&
                  Object.keys(fact.attributes).length > 0 ? (
                    <div style={{ marginTop: 4 }}>
                      {attributeSummary(fact).map((line, i) => (
                        <div key={i}>
                          <Text style={{ fontSize: 12 }} type="secondary">
                            {line}
                          </Text>
                        </div>
                      ))}
                      <details style={{ marginTop: 2 }}>
                        <summary
                          style={{
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: 11,
                          }}
                        >
                          raw
                        </summary>
                        <pre
                          style={{
                            fontSize: 11,
                            margin: '4px 0 0',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {JSON.stringify(fact.attributes, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ) : null}
                </div>
              ),
            };
          })}
      />
    );
  }

  const title = (
    <Space>
      <span>Current state:</span>
      {data?.current_state ? (
        <Tag color={eventColor(data.current_state)}>
          {eventLabel(data.current_state)}
        </Tag>
      ) : (
        <Text type="secondary">none</Text>
      )}
    </Space>
  );

  return (
    <Card
      extra={
        <Button
          disabled={loading || !data || data.current_state === 'transacted'}
          onClick={() => {
            setTransactReason('');
            setTransactDate(null);
            setTransactPrice(null);
            setTransactResult(null);
            setTransactOpen(true);
          }}
          size="small"
        >
          {data?.current_state === 'transacted'
            ? 'Transacted'
            : 'Mark transacted'}
        </Button>
      }
      style={{ marginTop: 16 }}
      title={title}
    >
      {body}
      <Modal
        footer={
          ranResult || blocked
            ? [
                <Button key="close" onClick={closeModal}>
                  Close
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={closeModal}>
                  Cancel
                </Button>,
                <Button
                  danger
                  disabled={!reason.trim()}
                  key="run"
                  loading={submitting}
                  onClick={() => void submit()}
                  type="primary"
                >
                  Confirm undo
                </Button>,
              ]
        }
        onCancel={closeModal}
        open={target !== null}
        title={target ? `Undo "${target.event}" fact` : ''}
      >
        {ranResult ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message={
                result!.status === 'ok'
                  ? 'Undone — all effects applied.'
                  : result!.status === 'partial'
                    ? 'Ledger corrected, but an effect failed — needs a manual follow-up.'
                    : 'Failed.'
              }
              type={
                result!.status === 'ok'
                  ? 'success'
                  : result!.status === 'partial'
                    ? 'warning'
                    : 'error'
              }
            />
            {(result!.steps ?? []).map((s, i) => (
              <div key={i}>
                <Text type={s.status === 'ok' ? 'success' : 'danger'}>
                  {s.status === 'ok' ? '✓' : '✗'} {s.name}
                </Text>
                {s.detail ? <Text type="secondary"> — {s.detail}</Text> : null}
              </div>
            ))}
          </Space>
        ) : blocked ? (
          <Alert
            message={result!.block_reason ?? 'Cannot undo.'}
            type="error"
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              This is consequential and will undo the recorded state. A reason
              is required.
            </Text>
            <TextArea
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (required)"
              rows={3}
              value={reason}
            />
          </Space>
        )}
      </Modal>
      <Modal
        footer={
          transactResult
            ? [
                <Button key="close" onClick={() => setTransactOpen(false)}>
                  Close
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={() => setTransactOpen(false)}>
                  Cancel
                </Button>,
                <Button
                  key="run"
                  loading={transactSubmitting}
                  onClick={() => void submitTransact()}
                  type="primary"
                >
                  Mark transacted
                </Button>,
              ]
        }
        onCancel={() => setTransactOpen(false)}
        open={transactOpen}
        title="Mark lead as transacted"
      >
        {transactResult && transactResult.status === 'blocked' ? (
          <Alert
            message={transactResult.block_reason ?? 'Cannot mark transacted.'}
            type="error"
          />
        ) : transactResult ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message={
                transactResult.status === 'ok'
                  ? 'Recorded — lead marked transacted.'
                  : transactResult.status === 'partial'
                    ? 'Recorded, but an effect failed — needs a manual follow-up.'
                    : (transactResult.block_reason ?? 'Failed.')
              }
              type={
                transactResult.status === 'ok'
                  ? 'success'
                  : transactResult.status === 'partial'
                    ? 'warning'
                    : 'error'
              }
            />
            {(transactResult.steps ?? []).map((s, i) => (
              <div key={i}>
                <Text type={s.status === 'ok' ? 'success' : 'danger'}>
                  {s.status === 'ok' ? '✓' : '✗'} {s.name}
                </Text>
                {s.detail ? <Text type="secondary"> — {s.detail}</Text> : null}
              </div>
            ))}
          </Space>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text type="secondary">
              Records that this lead bought and/or sold with the agent, for
              reporting attribution. Reason, close date, and price are all
              optional. This does not contact the lead.
            </Text>
            <TextArea
              onChange={e => setTransactReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
              value={transactReason}
            />
            <Space wrap>
              <DatePicker
                onChange={d =>
                  setTransactDate(d ? d.format('YYYY-MM-DD') : null)
                }
                placeholder="Close date (optional)"
                value={transactDate ? dayjs(transactDate) : null}
              />
              <InputNumber
                min={0}
                onChange={v =>
                  setTransactPrice(typeof v === 'number' ? v : null)
                }
                placeholder="Deal price (optional)"
                prefix="$"
                style={{ width: 200 }}
                value={transactPrice ?? undefined}
              />
            </Space>
          </Space>
        )}
      </Modal>
      <Modal
        footer={
          flagResult
            ? [
                <Button key="close" onClick={closeFlagModal}>
                  Close
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={closeFlagModal}>
                  Cancel
                </Button>,
                <Button
                  disabled={
                    flagAction === 'flag' && flagCategories.length === 0
                  }
                  key="run"
                  loading={flagSubmitting}
                  onClick={() => void submitFlagAction()}
                  type="primary"
                >
                  {flagAction === 'flag' ? 'Mark bad transfer' : 'Remove flag'}
                </Button>,
              ]
        }
        onCancel={closeFlagModal}
        open={flagTarget !== null}
        title={
          flagAction === 'flag'
            ? 'Mark potentially bad transfer'
            : 'Remove attention flag'
        }
      >
        {flagResult ? (
          <Alert
            message={flagResult.message}
            type={flagResult.ok ? 'success' : 'error'}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              {flagAction === 'flag'
                ? "Marks this transfer as needing human attention. Nothing is undone and the lead's state does not change — it only flags the lead for review."
                : 'Removes the attention flag from this transfer.'}
            </Text>
            {flagAction === 'flag' ? (
              <Select<TransferFlagReason[]>
                mode="multiple"
                onChange={value => setFlagCategories(value)}
                placeholder="Categories (at least one required)"
                style={{ width: '100%' }}
                value={flagCategories}
              >
                {TRANSFER_FLAG_REASONS.map(category => (
                  <Select.Option key={category} value={category}>
                    {TRANSFER_FLAG_REASON_LABELS[category]}
                  </Select.Option>
                ))}
              </Select>
            ) : null}
            <TextArea
              onChange={e => setFlagReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
              value={flagReason}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
}
